import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface StudentDetail {
  student_code: string;
  student_name: string | null;
  student_nickname: string | null;
  total_classes: number;
  total_hours: number;
  current_hours: number | null;
  status: 'renewed' | 'depleted' | 'nearly_depleted' | 'not_renewed' | 'active';
  total_purchase: number | null;
}

export interface TeacherStudentDetailsSummary {
  total_students: number;
  total_classes: number;
  total_hours: number;
  renewed_count: number;
  depleted_count: number;
  nearly_depleted_count: number;
  not_renewed_count: number;
  renewal_rate: number;
}

export interface TeacherStudentDetailsResponse {
  summary: TeacherStudentDetailsSummary;
  students: StudentDetail[];
}

interface UseTeacherStudentDetailsParams {
  teacherCode: string;
  startDate?: string;
  endDate?: string;
  enabled: boolean;
}

export function useTeacherStudentDetails({
  teacherCode,
  startDate,
  endDate,
  enabled,
}: UseTeacherStudentDetailsParams) {
  return useQuery<TeacherStudentDetailsResponse>({
    queryKey: ['teacher-student-details', teacherCode, startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('get-teacher-student-details', {
        body: { teacherCode, startDate, endDate },
      });

      if (error) {
        console.error('Error fetching teacher student details:', error);
        throw error;
      }

      return data as TeacherStudentDetailsResponse;
    },
    enabled: enabled && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
